import { Button, Box, TextField } from '@mui/material';
import AddIcon from '@mui/icons-material/Queue';
import PauseIcon from '@mui/icons-material/Pause';
import ResumeIcon from '@mui/icons-material/PlayArrow';
import { useCallback, useEffect, useState } from 'react';
import * as service from '../../services/jobs-service';
import ConfirmationDialog from '../../components/ConfirmationDialog/ConfirmationDialog';
import { QueueJobs } from './QueueJobs';
import { GridActionsCellItem, Table } from '../../components/table/Table';
import config from '../../config';

export function Queues() {
  const [loading, setLoading] = useState(true);
  const [queues, setQueues] = useState();
  const [addJobTo, setAddJobTo] = useState();
  const [showJobsFor, setShowJobsFor] = useState();

  useEffect(() => {
    const fetchData = async () => {
      if (showJobsFor) return;
      setLoading(true);
      service
        .getQueues()
        .then(setQueues)
        .finally(() => setLoading(false));
    };
    fetchData();
    const intervalId = setInterval(
      fetchData,
      config.isDevelopment ? 60000 : 5000,
    );
    return () => clearInterval(intervalId);
  }, [showJobsFor]);

  const setQueuePaused = useCallback((id, isPaused) => {
    setQueues((current) =>
      current.map((queue) =>
        queue.id === id ? { ...queue, isPaused } : queue,
      ),
    );
  }, []);

  const pauseQueue = useCallback(
    (id) => service.pauseQueue(id).then(() => setQueuePaused(id, true)),
    [setQueuePaused],
  );

  const resumeQueue = useCallback(
    (id) => service.resumeQueue(id).then(() => setQueuePaused(id, false)),
    [setQueuePaused],
  );

  return (
    <Box sx={{ p: 2, height: '100%' }}>
      {buildShowJobsDialog()}
      {buildAddJobDialog()}
      <Table
        loading={loading}
        columns={buildColumns()}
        rows={queues || []}
        initialState={{ sorting: { sortModel: [{ field: 'id' }] } }}
        hideFooter={false}
      />
    </Box>
  );

  function buildShowJobsDialog() {
    if (showJobsFor) {
      return (
        <QueueJobs {...showJobsFor} onCancel={() => setShowJobsFor(null)} />
      );
    }
  }

  function buildAddJobDialog() {
    if (!addJobTo) return;

    return (
      <ConfirmationDialog
        titleIcon={false}
        title="Add job"
        subTitle={`Add a job to the ${addJobTo} queue`}
        cancelButtonText="Cancel"
        continueButtonText="Add"
        handleClose={() => setAddJobTo(null)}
        handleConfirm={async (data) => {
          await service.runJob(addJobTo, data);
          service.getQueues().then(setQueues);
          setAddJobTo(null);
        }}
        renderBody={(setConfirmPayload) => (
          <TextField
            sx={{ mt: 1, width: '100%' }}
            label="Job Data"
            multiline
            rows={3}
            variant="outlined"
            onChange={(event) => setConfirmPayload(event.target.value)}
          />
        )}
      />
    );
  }

  function renderJobCount(type, { row }) {
    if (!row[type]) return 0;

    return (
      <Button
        color="inherit"
        sx={{
          justifyContent: 'flex-end',
          padding: 0,
          textDecoration: 'underline',
        }}
        onClick={() => setShowJobsFor({ queue: row.id, type })}
      >
        {row[type]}
      </Button>
    );
  }

  function buildColumns() {
    return [
      {
        field: 'id',
        headerName: 'Queue',
        flex: 1,
      },
      {
        field: 'waiting',
        headerName: 'Waiting',
        type: 'number',
        valueGetter: ({ row }) => row.waiting,
        renderCell: renderJobCount.bind(null, 'waiting'),
      },
      {
        field: 'active',
        headerName: 'Active',
        type: 'number',
        valueGetter: ({ row }) => row.active,
        renderCell: renderJobCount.bind(null, 'active'),
      },
      {
        field: 'completed',
        headerName: 'Completed',
        type: 'number',
        valueGetter: ({ row }) => row.completed,
        renderCell: renderJobCount.bind(null, 'completed'),
      },
      {
        field: 'failed',
        headerName: 'Failed',
        type: 'number',
        valueGetter: ({ row }) => row.failed,
        renderCell: renderJobCount.bind(null, 'failed'),
      },
      {
        field: 'delayed',
        headerName: 'Delayed',
        type: 'number',
        valueGetter: ({ row }) => row.delayed,
        renderCell: renderJobCount.bind(null, 'delayed'),
      },
      {
        field: 'paused',
        headerName: 'Paused',
        type: 'number',
        valueGetter: ({ row }) => row.paused,
        renderCell: renderJobCount.bind(null, 'paused'),
      },
      {
        field: 'actions',
        type: 'actions',
        headerName: 'Actions',
        getActions: ({ row }) => [
          <GridActionsCellItem
            icon={<AddIcon />}
            label="Add Job"
            onClick={() => setAddJobTo(row.id)}
          />,
          <GridActionsCellItem
            icon={row.isPaused ? <ResumeIcon /> : <PauseIcon />}
            label={`${row.isPaused ? 'Resume' : 'Pause'} Queue`}
            onClick={() =>
              row.isPaused ? resumeQueue(row.id) : pauseQueue(row.id)
            }
          />,
        ],
      },
    ];
  }
}
